import deepEqual from "deep-equal";
import { deepClone } from "#lib/deepClone.js";

export interface Suggestion<V> {
	word: string;
	value?: V;
}

class TrieNode<V> {
	key: string;
	values: V[];
	children: Record<string, TrieNode<V>>;
	isEndOfWord: boolean;

	constructor(key: string = "", value?: V) {
		this.children = {};
		this.isEndOfWord = false;
		this.key = key;
		this.values = value ? [value] : [];
	}
}

export class Trie<V> {
	private root: TrieNode<V>;

	constructor(root?: TrieNode<V>) {
		this.root = root ?? new TrieNode();
	}

	add(word: string, value: V) {
		const str = word.toLowerCase();
		let current = this.root;
		for (const character of str) {
			if (!(character in current.children)) {
				current.children[character] = new TrieNode(character);
			}
			current = current.children[character];
		}
		const index = current.values.findIndex(v => deepEqual(v, value));
		if (index === -1) {
			current.values.push(value);
		}
		current.isEndOfWord = true;
	}

	find(word: string): V[] | undefined {
		const str = word.toLowerCase();
		let current = this.root;
		for (const character of str) {
			if (!(character in current.children)) {
				return undefined;
			}
			current = current.children[character];
		}
		return current.isEndOfWord ? current.values : undefined;
	}

	findAll(phrase: string = "", current: TrieNode<V> = this.root): Suggestion<V>[] {
		const suggestions: Suggestion<V>[] = [];
		const seen: Set<string> = new Set();
		function backtracking(phrase: string, current: TrieNode<V>) {
			if (current.isEndOfWord) {
				for (const value of current.values) {
					suggestions.push({ word: `${phrase}`, value });
					seen.add(phrase);
				}
			}
			for (const [_, child] of Object.entries(current.children)) {
				backtracking(phrase + child.key, child);
			}
		}
		const str = phrase.toLowerCase();
		backtracking(str, current);
		return suggestions;
	}

	suggest(initials: string): Suggestion<V>[] {
		let count = 0;
		let current = this.root;
		while (initials.length > count) {
			if (!current.children[initials[count]]) {
				return [];
			}
			current = current.children[initials[count]];
			count++;
		}
		return this.findAll(initials.toLowerCase(), current);
	}

	clone(): Trie<V> {
		return new Trie(deepClone(this.root));
	}
}
